// Predict the CLOSE, not the result.
//
// Every well-identified study of betting markets says the same thing: almost
// nothing beats a liquid closing line. Elo and Dixon-Coles are fitted on who
// won, and on that label they lose to the market by a tenth of a nat and print
// nothing (t = 0.23 on AFL 2025). That is a loss-function problem, not a data
// problem — Hubáček & Šír (IJF 2023): a model trained to predict where the
// price will END UP, applied at the open, produces closing-line value; a model
// trained on outcomes does not.
//
// So this model has a different target. It does not say who wins. It says where
// the price is going: given the opening board, a sharp reference where one
// exists, how far the sharp price sits from the soft one, and how long there is
// until the jump, what will the devigged CLOSING probability be?
//
// FEATURES, per match and side, all read at the opening capture:
//
//   logit(pOpen)      the local board's opening price, devigged and averaged.
//                     Power on a fixture, Shin on a race — `defaultDevig`, the
//                     same choice every other reading in the app makes.
//   logit(pSharp)     the exchange (net of commission) or Pinnacle, if either
//                     has an opening row. Zero where neither does.
//   hasSharp          1 where that reading is real, 0 where it is a stand-in.
//                     Without it the fit would read "no sharp book" as "the
//                     sharp book agrees exactly with the locals", which is a
//                     claim nobody made.
//   spread            logit(pSharp) − logit(pOpen), the sharp-minus-soft gap.
//                     Zero with no sharp reading, for the same reason.
//   logHours          ln(1 + hours from the opening capture to the jump). A
//                     line posted eight days out has further to travel than one
//                     posted at lunchtime.
//   sport one-hot     over the sports the fit actually saw. An unseen sport is
//                     all zeros, which is the fit's prior and not an error.
//
// TARGET: the devigged CLOSING probability. Sharp first — the exchange or
// Pinnacle closing row, net of commission — and the local board's closing
// consensus where there is no sharp close. Nothing is excluded from that
// consensus: the target is where the market ended up, not where one book ended
// up relative to the others.
//
// FIT: ridge logistic by IRLS, exactly as `encompassing.js` does it, on
// standardised features. The response is a probability rather than a 0/1, which
// the Bernoulli likelihood takes without complaint (a fractional response
// quasi-likelihood: the score equations are X'(y − mu) either way). λ is small
// and only there so a season where every match has a sharp book cannot produce
// a singular design.
//
// PREDICT: the same feature builder over the prices in front of it. On a live
// board there are no `opening` rows yet, so "now" stands in for the open — which
// is the honest reading: the question is where this price is going from here.
//
// AND IT STILL GOES THROUGH THE GATE. Predicting the close is not permission to
// bet. The encompassing fit (`encompassing.js`) asks whether this model, GIVEN
// the market's current price, predicts RESULTS better than the market does, and
// until it clears t = 1.96 on a walk-forward sample the Close chaser prints
// nothing. That is the honest bar: a model that predicts the close perfectly
// and results no better than the board is a model that has told you the market
// moves, which everybody knew.

import { devig } from '../devig.js';
import { COMMISSION, EXCHANGES, netPrice } from '../exchange.js';
import { isRacingSport } from '../teams.js';
import { assertBefore } from './base.js';
import { fitRidge, logit, predictRidge, standardise } from './ridge.js';

/** The books whose price is taken as the sharp reference, in the order asked. */
export const SHARP_BOOKS = ['betfair_ex_au', 'pinnacle'];

/** A fixture's sides, in the order a price vector is built in. */
const SIDES = ['home', 'draw', 'away'];

/** Shin on a race, power on a fixture — the app's one answer, restated here. */
const devigFor = (sport) => (isRacingSport(sport) ? 'shin' : 'power');

/** Below this many rows the ridge is fitting noise, and no weights are kept. */
export const MIN_FIT_ROWS = 200;

/** The ridge penalty on the standardised features. Small: it breaks ties, nothing more. */
const LAMBDA = 1e-3;

/** A price vector nobody would print: a round under −20% or over 150%. */
const OVERROUND_BAND = [0.8, 2.5];

const DEFAULT_PARAMS = { lambda: LAMBDA, commission: COMMISSION, minRows: MIN_FIT_ROWS };

// --- reading a board, whichever shape it arrives in ---------------------------
//
// A fit is handed STORED history, where `prices` is a flat array of rows each
// carrying its own `selection` and `kind`. A prediction is handed a UNIVERSE
// match, where `prices` is `{ selection: [rows] }` and every row is `live`. One
// model, two shapes, and the difference is four lines rather than two feature
// builders that can drift apart.

/** Every price row on a match, each with a `selection`, whichever shape it came in. */
export function priceRows(match) {
  const prices = match?.prices;
  if (!prices) return [];
  if (Array.isArray(prices)) return prices;
  const out = [];
  for (const [selection, rows] of Object.entries(prices)) {
    for (const row of rows ?? []) out.push(row.selection ? row : { ...row, selection });
  }
  return out;
}

/** A race is priced in `win`, a fixture in `h2h`. Reading one out of the other is a blank season. */
const marketOf = (match) => (match?.runners ? 'win' : 'h2h');

/** The sides a price vector is built over, in a fixed order. */
function sidesOf(match, rows) {
  if (match?.runners) {
    const named = match.selections ?? Object.keys(match.runners);
    return [...named].sort();
  }
  const seen = new Set(rows.map((r) => r.selection));
  return SIDES.filter((s) => seen.has(s));
}

/**
 * What kind of capture a row is. A row with no kind at all is a live board —
 * the only place that happens is a universe match built from today's feed.
 */
const kindOf = (row) => row?.kind ?? 'live';

/**
 * One book's devigged probabilities over `sides` for one kind of capture, or
 * null where that book did not price every side. The exchange is deflated
 * first, for the reason `exchange.js` gives: a 2.00 back does not pay 2.00, and
 * devigging the quote reads the exchange as more generous than it is.
 */
function bookProbs(rows, sides, bookmaker, kinds, method, commission) {
  const vector = [];
  for (const side of sides) {
    const row = rows.find((r) => r.bookmaker === bookmaker && r.selection === side && kinds.has(kindOf(r)));
    const price = row ? netPrice(row.price, bookmaker, commission) : null;
    if (!Number.isFinite(price) || price <= 1) return null;
    vector.push(price);
  }
  const sum = vector.reduce((acc, p) => acc + 1 / p, 0);
  if (!(sum >= OVERROUND_BAND[0] && sum <= OVERROUND_BAND[1])) return null;
  const { probs } = devig(vector, method);
  const out = {};
  sides.forEach((side, i) => { out[side] = probs[i]; });
  return out;
}

/** Which kinds stand in for "the opening price" on this board. */
const openKinds = (rows) => (rows.some((r) => kindOf(r) === 'opening')
  ? new Set(['opening'])
  : new Set(['live', 'at']));

/** The board averaged: every book that priced the whole market, one vote each. */
function boardProbs(rows, sides, kinds, method, commission, { only = null } = {}) {
  const books = [...new Set(rows.filter((r) => kinds.has(kindOf(r))).map((r) => r.bookmaker))]
    .filter((b) => (only ? only.includes(b) : true))
    .sort();
  const votes = [];
  for (const book of books) {
    const probs = bookProbs(rows, sides, book, kinds, method, commission);
    if (probs) votes.push(probs);
  }
  if (!votes.length) return null;
  const out = {};
  for (const side of sides) {
    out[side] = votes.reduce((sum, v) => sum + v[side], 0) / votes.length;
  }
  return out;
}

/** The first sharp book with a complete vector of this kind, or null. */
function sharpProbs(rows, sides, kinds, method, commission) {
  for (const book of SHARP_BOOKS) {
    const probs = bookProbs(rows, sides, book, kinds, method, commission);
    if (probs) return probs;
  }
  return null;
}

/** When the opening capture was taken, or the kickoff where no row says. */
function openedAt(rows, kinds) {
  const times = rows
    .filter((r) => kinds.has(kindOf(r)))
    .map((r) => Date.parse(r.capturedAt ?? ''))
    .filter(Number.isFinite);
  return times.length ? Math.min(...times) : null;
}

// --- the features -------------------------------------------------------------

/** The names of the non-sport features, in the order the row is built. */
export const BASE_FEATURES = ['logitOpen', 'logitSharp', 'hasSharp', 'spread', 'logHours'];

/**
 * The opening (or live) devigged probabilities on a match, one entry per side,
 * plus the sharp reading beside them and when the capture was taken. Null where
 * the board has nothing complete on it.
 */
export function openingView(match, { commission = COMMISSION } = {}) {
  const rows = priceRows(match).filter((r) => (r.market ?? 'h2h') === marketOf(match));
  const sides = sidesOf(match, rows);
  if (sides.length < 2) return null;
  const kinds = openKinds(rows);
  const method = devigFor(match?.sport);
  const board = boardProbs(rows, sides, kinds, method, commission);
  if (!board) return null;
  return {
    sides,
    board,
    sharp: sharpProbs(rows, sides, kinds, method, commission),
    capturedAt: openedAt(rows, kinds),
    basis: kinds.has('opening') ? 'opening' : 'live',
  };
}

/**
 * The TARGET: the devigged closing probability per side. Sharp first, the local
 * board's own close second, and null where the history carries no closing rows
 * at all — which is every season ingested from a source that only kept one
 * capture, and the reason this model prints nothing on them.
 */
export function closingView(match, { commission = COMMISSION } = {}) {
  const rows = priceRows(match).filter((r) => (r.market ?? 'h2h') === marketOf(match));
  const sides = sidesOf(match, rows);
  if (sides.length < 2) return null;
  const kinds = new Set(['closing']);
  const method = devigFor(match?.sport);
  const sharp = sharpProbs(rows, sides, kinds, method, commission);
  const board = sharp ?? boardProbs(rows, sides, kinds, method, commission);
  if (!board) return null;
  return { sides, board, fromSharp: !!sharp };
}

/** The devigged opening probability for one side, or null. */
export function openProbability(match, selection, opts = {}) {
  const view = openingView(match, opts);
  const p = view?.board?.[selection];
  return Number.isFinite(p) ? p : null;
}

/** The devigged closing probability for one side, or null. */
export function closeProbability(match, selection, opts = {}) {
  const view = closingView(match, opts);
  const p = view?.board?.[selection];
  return Number.isFinite(p) ? p : null;
}

/**
 * One feature row for one side of one match, or null where the board cannot
 * answer. `sports` is the one-hot's alphabet, carried on the state so a fit and
 * a prediction build the same width of row.
 */
export function featureRow(match, selection, sports, { commission = COMMISSION, view = null } = {}) {
  const open = view ?? openingView(match, { commission });
  const pOpen = open?.board?.[selection];
  if (!Number.isFinite(pOpen) || pOpen <= 0 || pOpen >= 1) return null;

  const pSharp = open.sharp?.[selection];
  const hasSharp = Number.isFinite(pSharp) && pSharp > 0 && pSharp < 1;
  const lOpen = logit(pOpen);
  const lSharp = hasSharp ? logit(pSharp) : 0;

  const kickoff = Date.parse(match?.kickoff ?? '');
  const hours = Number.isFinite(kickoff) && Number.isFinite(open.capturedAt)
    ? Math.max(0, (kickoff - open.capturedAt) / 3_600_000)
    : 0;

  const x = [lOpen, lSharp, hasSharp ? 1 : 0, hasSharp ? lSharp - lOpen : 0, Math.log(1 + hours)];
  for (const sport of sports) x.push(match?.sport === sport ? 1 : 0);
  return x;
}

// --- the model ----------------------------------------------------------------
//
// The ridge logistic itself now lives in `./ridge.js`, which is where
// `cricketToss.js` reads it from too. It moved out of this file unchanged: the
// fractional response this model needs is exactly what an IRLS on the Bernoulli
// score equations already does.

/** Every sport in a history, sorted, so a one-hot has a stable alphabet. */
const sportsIn = (rows) => [...new Set(rows.map((m) => m?.sport).filter(Boolean))].sort();

export const closeLine = {
  id: 'close-line',
  version: 1,
  // Any sport whose history carries both an opening and a closing row per book.
  // Nothing narrower is honest: the model is about a market's own drift, and
  // the question of which markets drift is what the fit is for.
  sports: null,
  needsFit: true,
  defaultParams: { ...DEFAULT_PARAMS },

  /**
   * Walk the history, build one row per side of every match that carries both an
   * opening board and a closing one, and ridge-regress the closing probability
   * on the opening features.
   *
   * `asOf` is the moment the state claims to be true at, and `strict` refuses
   * outright rather than filtering — a future match in a walk-forward training
   * set is a bug in the harness, not a row to drop.
   */
  fit(history, asOf, params = {}, { strict = false } = {}) {
    const p = { ...DEFAULT_PARAMS, ...params };
    const matches = assertBefore(history, asOf, strict);
    const sports = sportsIn(matches);
    const width = BASE_FEATURES.length + sports.length;

    const rows = [];
    let withClose = 0;
    for (const match of matches) {
      const close = closingView(match, { commission: p.commission });
      if (!close) continue;
      withClose += 1;
      const open = openingView(match, { commission: p.commission });
      // A board whose only capture IS the close has no opening to learn from.
      if (!open || open.basis !== 'opening') continue;
      for (const side of close.sides) {
        const y = close.board[side];
        if (!Number.isFinite(y) || y <= 0 || y >= 1) continue;
        const x = featureRow(match, side, sports, { commission: p.commission, view: open });
        if (!x) continue;
        rows.push({ x, y });
      }
    }

    const { means, sds } = standardise(rows, width);
    const scaled = rows.map(({ x, y }) => ({
      x: x.map((v, i) => (v - means[i]) / sds[i]),
      y,
    }));
    const weights = scaled.length >= p.minRows ? fitRidge(scaled, width, p.lambda) : null;

    return {
      weights,
      means,
      sds,
      sports,
      n: rows.length,
      matches: withClose,
      params: p,
      fittedTo: new Date(asOf).toISOString(),
    };
  },

  /**
   * The expected closing probability for one side, from the prices in front of
   * it. Null where the board cannot be read, or where the fit never had enough
   * rows to be a fit — a model with no weights says nothing rather than
   * answering with the market's own price wearing a hat.
   */
  predict(state, match, selection) {
    const weights = state?.weights;
    if (!Array.isArray(weights) || !weights.length) return null;
    const sports = state.sports ?? [];
    const commission = state.params?.commission ?? COMMISSION;
    const x = featureRow(match, selection, sports, { commission });
    if (!x || x.length !== weights.length - 1) return null;
    return predictRidge(weights, x, state.means ?? [], state.sds ?? []);
  },
};

/** Re-exported so nothing else has to name the exchange list to read one. */
export { EXCHANGES };
